import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSortSelections } from '../slice/selectors';

interface ColumnConfig {
  key: string;
  [key: string]: any;
}

/**
 * Hook to reorder columns based on pin selections
 *
 * Pin behavior:
 * - Pin to Left: Moves column right after "name" column (leftmost position)
 * - Pin to Right: Moves column to the last position (rightmost)
 * - Only one column can be pinned at a time (enforced by Redux)
 *
 * @param columns - Original columns configuration
 * @returns Reordered columns based on active pin selection
 */
export const usePinnedColumns = <T extends ColumnConfig>(columns: T[]): T[] => {
  const sortSelections = useSelector(selectSortSelections);

  return useMemo(() => {
    const sharedPin = sortSelections.sharedPin;
    const sharedPinColumn = sortSelections.sharedPinColumn;

    // No pin active - return original order
    if (!sharedPin || !sharedPinColumn) {
      return columns;
    }

    // Find the pinned column
    const pinnedColumnIndex = columns.findIndex(
      col => col.key === sharedPinColumn
    );

    // Column not found - return original order
    if (pinnedColumnIndex === -1) {
      return columns;
    }

    // Create a copy of columns to avoid mutation
    const reorderedColumns = [...columns];
    const [pinnedColumn] = reorderedColumns.splice(pinnedColumnIndex, 1);

    // Pin to Left: Insert after "name" column (index 1, right after riskLevel + name)
    if (sharedPin === 'pinLeft') {
      const nameIndex = reorderedColumns.findIndex(col => col.key === 'name');
      if (nameIndex !== -1) {
        // Insert right after name
        reorderedColumns.splice(nameIndex + 1, 0, pinnedColumn);
      } else {
        // Fallback: insert at beginning if name not found
        reorderedColumns.unshift(pinnedColumn);
      }
    }
    // Pin to Right: Insert at the end
    else if (sharedPin === 'pinRight') {
      reorderedColumns.push(pinnedColumn);
    }

    return reorderedColumns;
  }, [columns, sortSelections.sharedPin, sortSelections.sharedPinColumn]);
};
